import { useState, useEffect, useCallback, useRef } from "react";
import { useAdminApi } from "../adminUtils";
import "./signatures.css";

const EMPTY = { _id: null, label: "", signOff: "Warmly,", name: "", title: "", photo: "", phone: "", email: "", website: "" };

// Centre-crop to a 256px square JPEG for the round headshot
function squareImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 256;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 256, 256);
      ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, 256, 256);
      URL.revokeObjectURL(url);
      canvas.toBlob(b => b ? resolve(new File([b], "headshot.jpg", { type: "image/jpeg" })) : reject(new Error("Could not read image")), "image/jpeg", 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

export default function SignaturesSection() {
  const { API, AUTH, ADMIN_SECRET } = useAdminApi();
  const [list, setList]       = useState([]);
  const [form, setForm]       = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/signatures`, { headers: AUTH });
      const d = await r.json();
      setList(d.signatures ?? []);
    } catch { /* keep */ }
  }, [API, AUTH]);

  useEffect(() => { load(); }, [load]);

  // Live preview of the signature as it will appear in emails
  useEffect(() => {
    if (!form) return;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/signatures/render`, { method: "POST", headers: AUTH, body: JSON.stringify(form) });
        const d = await r.json();
        setPreview(d.html || "");
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form, API, AUTH]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickPhoto = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("image", await squareImage(file));
      const r = await fetch(`${API}/newsletter/images`, {
        method: "POST", headers: { Authorization: AUTH.Authorization || `Bearer ${ADMIN_SECRET}` }, body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Upload failed.");
      set("photo", d.url);
    } catch (ex) { setError(ex.message); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setError(null);
    if (!form.label.trim() || !form.name.trim()) { setError("Label and name are required."); return; }
    setBusy(true);
    try {
      const r = await fetch(form._id ? `${API}/signatures/${form._id}` : `${API}/signatures`, {
        method: form._id ? "PUT" : "POST", headers: AUTH, body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error ?? "Could not save."); return; }
      setForm(null);
      load();
    } catch { setError("Something went wrong."); }
    finally { setBusy(false); }
  };

  const makeDefault = async s => {
    await fetch(`${API}/signatures/${s._id}/default`, { method: "POST", headers: AUTH });
    load();
  };

  const remove = async s => {
    if (!window.confirm(`Delete the "${s.label}" signature?`)) return;
    await fetch(`${API}/signatures/${s._id}`, { method: "DELETE", headers: AUTH });
    if (form?._id === s._id) setForm(null);
    load();
  };

  return (
    <div className="sig-section">
      <p className="sig-help">Added to the end of newsletters and messages. Pick one per email; the default is pre-selected.</p>

      {list.map(s => (
        <div key={s._id} className="sig-row">
          <div className="sig-row-main">
            <span className="sig-row-label">{s.label}{s.isDefault && <span className="sig-default">Default</span>}</span>
            <span className="sig-row-meta">{s.name}{s.title ? ` · ${s.title}` : ""}</span>
          </div>
          <div className="sig-row-actions">
            <button className="adm-btn-ghost-sm" onClick={() => { setError(null); setForm({ ...EMPTY, ...s }); }}>Edit</button>
            {!s.isDefault && <button className="adm-btn-ghost-sm" onClick={() => makeDefault(s)}>Make default</button>}
            <button className="adm-btn-ghost-sm" onClick={() => remove(s)}>Delete</button>
          </div>
        </div>
      ))}
      {list.length === 0 && !form && <p className="adm-empty">No signatures yet.</p>}

      {!form && (
        <button className="adm-btn-primary sig-add" onClick={() => { setError(null); setForm({ ...EMPTY, label: list.length ? "" : "Owner" }); }}>
          + Add signature
        </button>
      )}

      {form && (
        <div className="sig-form">
          <div className="sig-grid">
            <label className="adm-form-label">Label (only you see this)
              <input className="adm-input" value={form.label} placeholder="Owner, Studio Manager…" onChange={e => set("label", e.target.value)} />
            </label>
            <label className="adm-form-label">Sign-off
              <input className="adm-input" value={form.signOff} placeholder="Warmly," onChange={e => set("signOff", e.target.value)} />
            </label>
            <label className="adm-form-label">Name
              <input className="adm-input" value={form.name} onChange={e => set("name", e.target.value)} />
            </label>
            <label className="adm-form-label">Role / title
              <input className="adm-input" value={form.title} placeholder="Owner & Senior Teacher" onChange={e => set("title", e.target.value)} />
            </label>
            <label className="adm-form-label">Phone
              <input className="adm-input" value={form.phone} onChange={e => set("phone", e.target.value)} />
            </label>
            <label className="adm-form-label">Email
              <input className="adm-input" type="email" value={form.email} onChange={e => set("email", e.target.value)} />
            </label>
            <label className="adm-form-label sig-wide">Website
              <input className="adm-input" value={form.website} placeholder="flourishyoga.studio" onChange={e => set("website", e.target.value)} />
            </label>
          </div>

          <div className="sig-photo">
            {form.photo ? <img src={form.photo} alt="" /> : <span className="sig-photo-empty">No photo</span>}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={pickPhoto} />
            <button type="button" className="adm-btn-ghost-sm" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? "Uploading…" : form.photo ? "Replace photo" : "Add photo (optional)"}
            </button>
            {form.photo && <button type="button" className="adm-btn-ghost-sm" onClick={() => set("photo", "")}>Remove photo</button>}
          </div>

          <div className="sig-preview">
            <span className="sig-preview-label">Preview</span>
            <iframe title="Signature preview" sandbox="" srcDoc={`<body style="margin:0;padding:4px 16px;background:#fff">${preview}</body>`} />
          </div>

          {error && <p className="adm-error">{error}</p>}
          <div className="sig-form-actions">
            <button className="adm-btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save signature"}</button>
            <button className="adm-btn-ghost-sm" onClick={() => { setForm(null); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
